import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Sparkles, Award, TrendingUp, Compass, GraduationCap, Route, Printer, ArrowRight, Landmark, MapPin, Timer } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell } from "recharts";
import { CATEGORY_META } from "@/lib/categoryMeta";

const AI_IMG =
  "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85";

const SAMPLE = {
  user_name: "Aarav",
  summary:
    "You show a strong investigative streak paired with realistic, hands-on interests — you learn best by doing and testing ideas rather than just reading about them. Your emotional stability and steady extraversion make you a natural fit for collaborative, field-based work.",
  path_alignment: "strong",
  top_careers: [
    {
      title: "Environmental Scientist",
      match_percent: 88,
      why: "Combines your investigative love for science with realistic outdoor interests. Teamwork in field research and lab analysis aligns perfectly with your collaborative nature.",
      typical_subjects: ["Biology", "Chemistry", "Geography", "Environmental Science"],
    },
    {
      title: "Laboratory Technician / Research Assistant",
      match_percent: 82,
      why: "Ideal for your investigative mindset and hands-on realistic preferences. Working in research teams conducting experiments leverages your emotional stability.",
      typical_subjects: ["Physics", "Chemistry", "Biology", "Mathematics"],
    },
    {
      title: "Sports Science Professional",
      match_percent: 76,
      why: "Merges your realistic love for outdoor adventures with investigative study of human performance. Suits your extraverted and balanced personality.",
      typical_subjects: ["Physical Education", "Biology", "Psychology", "Health Science"],
    },
  ],
  personality_analysis:
    "You're grounded and steady under pressure, with a natural curiosity that pulls you toward figuring out how things work. You'd rather test a hypothesis than take someone's word for it, and you're comfortable working alongside others as long as the work itself is substantive.",
  strengths: ["Methodical problem-solving", "Comfortable with fieldwork and labs", "Reliable under pressure", "Works well in small teams"],
  growth_areas: ["Public speaking confidence", "Delegating in group projects", "Long-form written communication"],
  recommended_stream: "Science (PCB/PCMB)",
  nep_alignment:
    "Your profile aligns well with NEP 2020's multidisciplinary approach — pairing core sciences with an environmental studies or data-skills elective would round out your strengths.",
  board_notes: "Prioritize practicals and lab-based internal assessments — they play directly to your strengths.",
  trait_scores: { Balanced: 3, Openness: 2, Extraversion: 2, "Emotional Stability": 3, Investigative: 4, Realistic: 3 },
  aptitude: { correct: 5, total: 6 },
  mental_ability: { correct: 3, total: 4 },
  roadmap: [
    { stage: "Grade 9–10", actions: ["Take up a science fair or field-research project", "Explore NCERT environmental science electives", "Shadow a lab technician or scientist for a day"] },
    { stage: "Grade 11–12", actions: ["Choose PCB/PCMB with an environmental science elective", "Apply for summer research internships", "Build a portfolio of field observations or lab work"] },
    { stage: "After Grade 12", actions: ["Pursue a B.Sc. in Environmental Science or a related field", "Look for research assistantships during undergrad", "Consider certifications in GIS or field survey methods"] },
  ],
  top_colleges: [
    { name: "TERI School of Advanced Studies", location: "New Delhi", why: "Specialized environmental science and sustainability programs.", how_to_get_in: "Strong Class 12 science scores + entrance test." },
    { name: "Wildlife Institute of India", location: "Dehradun", why: "Field-focused ecology and conservation research.", how_to_get_in: "Science background with a competitive entrance exam." },
    { name: "IISER Pune", location: "Pune", why: "Research-driven interdisciplinary science education.", how_to_get_in: "IISER Aptitude Test or JEE/NEET-based admission." },
  ],
  encouragement: "You've got a clear, grounded direction — the next step is just picking one field project to get your hands dirty in.",
  behavioral_insights:
    "You paced yourself thoughtfully — spending a little extra time on the aptitude and mental-ability questions, which often reflects careful, deliberate reasoning rather than uncertainty. You answered personality questions quickly and consistently, a sign you know yourself well.",
  behavior_metrics: { total_time_seconds: 312, avg_time_per_question: 15.6, total_answer_changes: 2, engagement_index: 86 },
  question_times: [
    { name: "Q1", seconds: 9, category: "personality" }, { name: "Q2", seconds: 11, category: "personality" },
    { name: "Q3", seconds: 8, category: "interest" }, { name: "Q4", seconds: 10, category: "interest" },
    { name: "Q5", seconds: 22, category: "aptitude" }, { name: "Q6", seconds: 26, category: "aptitude" },
    { name: "Q7", seconds: 19, category: "mental_ability" }, { name: "Q8", seconds: 15, category: "mental_ability" },
  ],
};

const ENGAGEMENT_BANDS = [
  { min: 80, label: "Focused", color: "bg-[#A7F3D0]" },
  { min: 55, label: "Deliberate", color: "bg-[#FEF08A]" },
  { min: 0, label: "Rushed", color: "bg-[#FFDDBF]" },
];
const engagementBand = (score) => ENGAGEMENT_BANDS.find((b) => score >= b.min) || ENGAGEMENT_BANDS[ENGAGEMENT_BANDS.length - 1];

export default function SampleReport() {
  const traitData = Object.entries(SAMPLE.trait_scores).map(([trait, value]) => ({ trait, value }));
  const band = engagementBand(SAMPLE.behavior_metrics.engagement_index);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div data-testid="sample-report-banner" className="mb-6 card-brutal p-4 flex items-center gap-3 bg-[#E9D5FF]">
          <Sparkles size={20} strokeWidth={2.5} className="shrink-0" />
          <div>
            <div className="label-mono">Sample AI Career Consulting Report</div>
            <div className="text-sm text-[#0A0A0A]/80 italic">
              This is an example report with sample data — take the real assessment to get yours.
            </div>
          </div>
        </div>

        <div className="print:hidden mb-4 flex flex-wrap justify-end gap-2">
          <Link to="/register" data-testid="sample-report-cta" className="btn-brutal bg-blue-600 text-white px-4 py-2 text-sm flex items-center gap-2">
            Get your own report <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
          <button data-testid="print-sample-report-btn" onClick={() => window.print()} className="btn-brutal bg-[#0A0A0A] text-white px-4 py-2 text-sm flex items-center gap-2">
            <Printer size={16} strokeWidth={2.5} /> Save / Print PDF
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          <div className="md:col-span-8 card-brutal p-8">
            <span className="label-mono flex items-center gap-2"><Sparkles size={14} strokeWidth={2.5} /> AI Career Report</span>
            <h1 data-testid="sample-report-title" className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter mt-3">
              Hey {SAMPLE.user_name}, here's your path.
            </h1>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-[#0A0A0A]">{SAMPLE.summary}</p>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 border-2 border-[#0A0A0A] rounded-full bg-[#A7F3D0]">
              <span className="label-mono">Strong alignment</span>
            </div>
          </div>
          <div className="md:col-span-4 card-brutal p-4 bg-[#E9D5FF]">
            <img src={AI_IMG} alt="AI matrix visualisation" className="w-full h-56 object-cover rounded-xl border-2 border-[#0A0A0A]" />
            <p className="mt-3 text-sm font-medium">AI-generated career report</p>
          </div>
        </div>

        <section className="mb-10">
          <span className="label-mono">Top matches</span>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">Careers that fit you</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SAMPLE.top_careers.map((c, i) => (
              <div key={i} data-testid={`sample-career-card-${i}`} className="card-brutal p-6">
                <div className="flex items-start justify-between">
                  <Award strokeWidth={2.5} size={26} />
                  <span className="label-mono bg-[#A7F3D0] px-2 py-1 border-2 border-[#0A0A0A] rounded-full">{c.match_percent}%</span>
                </div>
                <h3 className="font-display text-xl font-bold mt-3">{c.title}</h3>
                <p className="text-sm mt-2 text-[#52525B] leading-relaxed">{c.why}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {c.typical_subjects.map((s, k) => (
                    <span key={k} className="text-xs px-2 py-1 bg-[#FEF08A] border-2 border-[#0A0A0A] rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10">
          <div className="md:col-span-7 card-brutal p-8">
            <span className="label-mono flex items-center gap-2"><Compass size={14} strokeWidth={2.5} /> Personality</span>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mt-2">Who you are</h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed">{SAMPLE.personality_analysis}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-[#A7F3D0] border-2 border-[#0A0A0A] rounded-xl p-4">
                <div className="label-mono flex items-center gap-1"><TrendingUp size={12} strokeWidth={2.5} /> Strengths</div>
                <ul className="mt-2 space-y-1 text-sm">
                  {SAMPLE.strengths.map((s, i) => <li key={i}>• {s}</li>)}
                </ul>
              </div>
              <div className="bg-[#FFDDBF] border-2 border-[#0A0A0A] rounded-xl p-4">
                <div className="label-mono">Growth areas</div>
                <ul className="mt-2 space-y-1 text-sm">
                  {SAMPLE.growth_areas.map((s, i) => <li key={i}>• {s}</li>)}
                </ul>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 card-brutal p-6">
            <span className="label-mono">Your trait signature</span>
            <div className="h-72 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={traitData}>
                  <PolarGrid stroke="#0A0A0A" />
                  <PolarAngleAxis dataKey="trait" tick={{ fontSize: 11, fill: "#0A0A0A" }} />
                  <PolarRadiusAxis tick={false} />
                  <Radar dataKey="value" stroke="#2563EB" fill="#3B82F6" fillOpacity={0.4} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-2">
                <div className="label-mono">Aptitude</div>
                <div className="font-display font-extrabold">{SAMPLE.aptitude.correct}/{SAMPLE.aptitude.total}</div>
              </div>
              <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-2">
                <div className="label-mono">Mental Ability</div>
                <div className="font-display font-extrabold">{SAMPLE.mental_ability.correct}/{SAMPLE.mental_ability.total}</div>
              </div>
            </div>
          </div>
        </section>

        <section data-testid="sample-behavior-section" className="mb-10">
          <span className="label-mono flex items-center gap-2"><Timer size={14} strokeWidth={2.5} /> Behavior &amp; focus</span>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">How you took this assessment</h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-7 card-brutal p-8">
              <p className="text-sm md:text-base leading-relaxed">{SAMPLE.behavioral_insights}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                  <div className="label-mono">Total time</div>
                  <div className="font-display font-extrabold">
                    {Math.floor(SAMPLE.behavior_metrics.total_time_seconds / 60)}m {Math.round(SAMPLE.behavior_metrics.total_time_seconds % 60)}s
                  </div>
                </div>
                <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                  <div className="label-mono">Avg / question</div>
                  <div className="font-display font-extrabold">{SAMPLE.behavior_metrics.avg_time_per_question}s</div>
                </div>
                <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                  <div className="label-mono">Answer changes</div>
                  <div className="font-display font-extrabold">{SAMPLE.behavior_metrics.total_answer_changes}</div>
                </div>
                <div className={`border-2 border-[#0A0A0A] rounded-lg p-3 ${band.color}`}>
                  <div className="label-mono">Engagement</div>
                  <div className="font-display font-extrabold">{band.label}</div>
                </div>
              </div>
              <p className="text-xs text-[#52525B] mt-3">
                Engagement is an indicative signal based on pacing and answer changes — not a diagnostic score.
              </p>
            </div>

            <div className="md:col-span-5 card-brutal p-6">
              <span className="label-mono">Time per question</span>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={SAMPLE.question_times}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Bar dataKey="seconds">
                      {SAMPLE.question_times.map((d, idx) => (
                        <Cell key={idx} fill={CATEGORY_META[d.category]?.hex || "#3B82F6"} stroke="#0A0A0A" strokeWidth={2} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 card-brutal p-8 bg-[#FEF08A]">
            <span className="label-mono flex items-center gap-2"><GraduationCap size={14} strokeWidth={2.5} /> Recommended stream</span>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-3">{SAMPLE.recommended_stream}</h2>
            <div className="mt-5 bg-white border-2 border-[#0A0A0A] rounded-xl p-4">
              <span className="label-mono">NEP 2020 alignment</span>
              <p className="text-sm mt-2 leading-relaxed">{SAMPLE.nep_alignment}</p>
            </div>
          </div>
          <div className="card-brutal p-6 bg-[#A7F3D0]">
            <span className="label-mono">Board note</span>
            <p className="text-sm mt-3 leading-relaxed">{SAMPLE.board_notes}</p>
          </div>
        </section>

        <section className="mb-14">
          <span className="label-mono flex items-center gap-2"><Route size={14} strokeWidth={2.5} /> Your roadmap</span>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">What to do next</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SAMPLE.roadmap.map((s, i) => (
              <div key={i} className="card-brutal p-6">
                <span className="font-display text-4xl font-extrabold text-blue-600">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-lg font-bold mt-2">{s.stage}</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {s.actions.map((a, k) => <li key={k}>• {a}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <span className="label-mono flex items-center gap-2"><Landmark size={14} strokeWidth={2.5} /> Where to aim</span>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">Top colleges for your path</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SAMPLE.top_colleges.map((c, i) => (
              <div key={i} data-testid={`sample-college-card-${i}`} className="card-brutal p-6 bg-[#E9D5FF]">
                <Landmark strokeWidth={2.5} size={24} />
                <h3 className="font-display text-lg font-bold mt-3">{c.name}</h3>
                <div className="flex items-center gap-1 text-xs text-[#52525B] mt-1">
                  <MapPin size={12} strokeWidth={2.5} /> {c.location}
                </div>
                <p className="text-sm mt-3 text-[#0A0A0A]/80 leading-relaxed">{c.why}</p>
                <div className="mt-4 bg-white border-2 border-[#0A0A0A] rounded-xl p-3">
                  <span className="label-mono">How to get in</span>
                  <p className="text-sm mt-2 leading-relaxed">{c.how_to_get_in}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="card-brutal p-6 bg-[#A7F3D0] text-center">
          <p className="font-display text-lg font-bold">{SAMPLE.encouragement}</p>
        </div>

        <div className="mt-10 flex justify-center print:hidden">
          <Link to="/register" data-testid="sample-report-bottom-cta" className="btn-brutal bg-blue-600 text-white px-6 py-3 flex items-center gap-2">
            Get your own report <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
